import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { debounce, uniqBy } from "lodash";
import { styled } from "styled-components";

type IProps = {
  placeholder?: string;
  fetchList: any;
  onHandleChange: any;
  defaultValue?: any;
  value?: any;
  disabled?: boolean;
};

const ProductSearchableDropdown = ({
  placeholder = "Search",
  fetchList,
  onHandleChange,
  defaultValue,
  value,
  disabled = false,
}: IProps) => {
  const [options, setOptions] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);

  const fetchOptions = useCallback(async () => {
    if (options?.length < totalCount || options?.length === 0) {
      setLoading(true);
      try {
        const response = await fetchList({
          page,
          isActive: true,
          perPage: 100,
        });
        setTotalCount(response?.totalCount);
        const newOptions = response.data?.map((option: any) => ({
          label: option?.name,
          value: option?.id,
        }));
        setOptions((prevOptions: any) =>
          uniqBy([...prevOptions, ...newOptions], "value")
        );
        setPage(page + 1);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [page, fetchList, options, totalCount]);

  const handleInputChange = debounce((value) => {
    setSearch(value);
  }, 1000);

  useEffect(() => {
    fetchOptions();
  }, [search, fetchOptions]);
  return value ? (
    <StyledSelect
      classNamePrefix="react-select"
      onInputChange={handleInputChange}
      options={options}
      isLoading={loading}
      defaultValue={defaultValue}
      onMenuScrollToBottom={fetchOptions}
      onChange={onHandleChange}
      placeholder={placeholder}
      isSearchable
      isDisabled={disabled}
      value={{
        value: value,
        label: defaultValue,
      }}
    />
  ) : (
    <StyledSelect
      classNamePrefix="react-select"
      onInputChange={handleInputChange}
      options={options}
      isLoading={loading}
      defaultValue={defaultValue}
      onMenuScrollToBottom={fetchOptions}
      onChange={onHandleChange}
      placeholder={placeholder}
      isSearchable
      isDisabled={disabled}
    />
  );
};

export default ProductSearchableDropdown;

export const StyledSelect = styled(Select)`
  .react-select__control {
    background: var(--card-bg);
    border: 2px solid var(--card-border);
    border-radius: 12px;
    min-height: 52px;
    box-shadow: none;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: var(--accent);
    }

    &--is-focused {
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(217, 58, 53, 0.1);
    }
  }

  .react-select__value-container {
    padding: 8px 16px;
  }

  .react-select__placeholder {
    color: var(--text-secondary);
    font-family: Poppins;
    font-size: 14px;
  }

  .react-select__single-value {
    color: var(--text-primary);
    font-family: Poppins;
    font-size: 14px;
  }

  .react-select__input-container {
    color: var(--text-primary);
    font-family: Poppins;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: var(--text-secondary);
    padding: 8px 16px;

    &:hover {
      color: var(--accent);
    }
  }

  .react-select__menu {
    background: var(--card-bg);
    border: 2px solid var(--card-border);
    border-radius: 12px;
    box-shadow: 0 10px 40px var(--shadow-large);
    overflow: hidden;
    z-index: 9999;
    margin-top: 8px;
    position: absolute;
  }

  .react-select__menu-list {
    padding: 8px;
  }

  .react-select__option {
    background: transparent;
    color: var(--text-primary);
    font-family: Poppins;
    font-size: 14px;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &--is-focused {
      background: var(--bg-secondary);
    }

    &--is-selected {
      background: var(--accent);
      color: #fff;
    }

    &:active {
      background: var(--accent);
      color: #fff;
    }
  }

  .react-select__loading-indicator {
    color: var(--accent);
  }

  .react-select__clear-indicator {
    color: var(--text-secondary);

    &:hover {
      color: var(--accent);
    }
  }
` as typeof Select;
